import { useEffect, useState } from 'react';
import { brandName, EmptyState, type DeviceAccount } from '@dugzi/ui';
import { api } from '../lib/api';
import type { Me } from '../lib/types';

/**
 * A file or folder address this account can't open (DZ-10803). The API answers the same 404 whether the item is
 * someone else's or doesn't exist (tests/isolation_s4.rs), so this page never says which: it names the account in
 * use and offers the others signed in on this device, as Google Drive's "You need access" and OneDrive's permission
 * page do. Request access comes later; it needs a notification to the owner that can't be abused.
 */
export function NeedAccess({ me, kind, id, inline }: { me: Me; kind: 'file' | 'folder'; id: string; inline?: boolean }) {
  const [others, setOthers] = useState<DeviceAccount[]>([]);
  useEffect(() => {
    api<{ accounts: DeviceAccount[] }>('/api/accounts')
      .then((r) => setOthers(r.accounts.filter((a) => a.id !== me.user.id && !a.signedOut)))
      .catch(() => {});
  }, [me.user.id]);
  // The same address, opened as another account (tabAccount reads ?account=): the person lands where the link pointed.
  // Built from the id, never from location: a crafted path (//elsewhere) can't become a link off this site.
  const here = `/${kind === 'file' ? 'f' : 'folders'}/${encodeURIComponent(id)}`;
  const as = (a: DeviceAccount) => `${here}?account=${encodeURIComponent(a.id)}`;
  const signIn = `/api/auth/login?add=1&return_to=${encodeURIComponent(here)}`;
  document.title = `You need access · ${brandName()} Drive`;
  return (
    <div className={inline ? 'need-access' : 'boot need-access'} data-testid="need-access">
      <EmptyState scene="sensitive" title="You need access"
        action={<div className="need-access__actions">
          {others.map((a) => <a key={a.id} className="dz-btn" href={as(a)} data-testid="need-access-as">Open as {a.address}</a>)}
          <a className="dz-btn" href={signIn} data-testid="need-access-signin">Use another account</a>
          <a className="dz-btn dz-btn--ghost" href="/" data-testid="need-access-home">Go to My Drive</a>
        </div>}>
        <p>This {kind} isn’t shared with <strong>{me.user.email}</strong>, or it no longer exists.</p>
        <p>Ask the person who sent it to share it with this address, or open it with the account it was shared with.</p>
      </EmptyState>
    </div>
  );
}
